import React, { useRef, useState, useEffect, useCallback } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { resize } from 'utils/imageResizer';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { BorderedCloseIcon, SaveIcon } from 'Icons';
import COLOURS from 'utils/constants/colors';
import Image from 'components/Image';
import staticText from 'static/text_display.json';
import { images } from 'utils/icons';
import InputLabel from '@material-ui/core/InputLabel';
import { formLabels } from './options';
import { convertToBase64 } from 'utils/audioHelper';
import ReactAudioPlayer from './ReactAudioPlayer';
import { showSnackbar } from 'reducers/ui';
import { useDispatch } from 'react-redux';
import { MEDIA_TYPE_API_ENUMS } from 'utils/constants/media';
import { mediaUploader } from '../../../../utils/constants/snackbarText';
import { useTranslation } from 'react-i18next';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { FORM, MASK } from 'utils/constants/css';
import IconWithText from 'components/IconWithText/iconWithText';
import SingleAudioPlayer from 'components/Details/Media/singleAudioPlayer';

const WithColorSaveIcon = withStyles(theme => ({
  root: {
    color: theme.palette.text.primary,
  },
}))(SaveIcon);

const useStyles = makeStyles(theme => ({
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    margin: '0 !important',
  },
  addMedia: {
    position: 'absolute',
    width: '5rem',
    height: '5rem',
    margin: '0 !important',
  },

  imageLabel: {
    fontSize: '12px',
    color: COLOURS.GREY_01,
    marginBottom: '0.5rem',
  },
  input: {
    display: 'none',
  },
  label: {
    borderBottom: 0,
  },
  container: {
    display: 'flex',
    width: '10rem',
    height: '10rem',
    justifyContent: 'center',
    position: 'relative',
    alignItems: 'center',
    border: FORM.BORDER,
    borderRadius: FORM.BORDER_RADIUS_IMAGE,
  },
  icon: {
    zIndex: 499,
    position: 'absolute',
    right: '0',
    bottom: '0',
    opacity: '0.9',
    borderTop: FORM.BORDER,
    borderLeft: FORM.BORDER,
    borderRadius: FORM.BORDER_RADIUS_IMAGE,
    background: `${theme.palette.text.secondary} !important`,
    fill: `${theme.palette.text.primary} !important`,
  },

  header: {
    display: 'flex',
    width: '100%',
    background: theme.palette.primary.main,
    justifyContent: 'flex-end',
    top: '0px',
    alignItems: 'center',
    color: theme.palette.secondary.light,
  },
  cropContainer: {
    position: 'fixed',
    top: 0,
    bottom: '10%',
    left: 0,
    right: 0,
    margin: 'auto',
    zIndex: 50000,
    maxWidth: '65em', // This is the actual width of the react portal we use in this app
    height: '600px',
    flexDirection: 'column',
    color: theme.palette.secondary.light,
  },
  audioContainer: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    zIndex: 60000,
    width: '65em',
    height: '500px',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
  },
  cropButtons: {
    width: '50%',
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '1rem',
    flexDirection: 'row-reverse',
  },
  error: {
    color: COLOURS.ERROR,
    marginTop: '0.5rem',
    marginLeft: '0.5rem',
    fontSize: '10px',
  },

  canvas: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.9)',
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  fullHeight: {
    height: '100%',
    '& > div': {
      height: '100%', // react crop adds an extra div which doesn't allow image to be inside the parent and responsive
    },
  },
  mask: {
    position: 'absolute',
    opacity: MASK.OPACITY_LOW,
    left: 0,
    height: 'inherit',
    width: 'inherit',
    background: theme.palette.text.disabled,
    zIndex: 1000,
  },
}));
const originalDimensions = {
  unit: '%',
  width: 50,
  height: 50,
  x: 25,
  y: 25,
};

const DetailMediaUploader = ({
  onChange,
  value = [],
  label,
  error,
  allowDeleteRow = true,
  id = 'profileImage',
  displaySelectedMedia,
  mediaType,
  formProps,
  noOfMediaFilesAllowed,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const inputFile = useRef(null);
  const classes = useStyles();
  const [upImg, setUpImg] = useState(null);
  const [upAudio, setUpAudio] = useState(null);
  const [cropping, setCropping] = useState(false);
  const [crop, setCrop] = useState(originalDimensions);
  const imgRef = useRef(null);
  const [currentFileName, setCurrentFileName] = useState('');
  const dispatch = useDispatch();

  const textErrorOrBool = typeof error === 'string' ? t(error) : error;
  const snackBar = useCallback(
    ({ typeOfSnackbar, message }) =>
      dispatch(showSnackbar({ typeOfSnackbar, message })),
    [dispatch],
  );

  useEffect(() => {
    return () => {
      setUpImg(null);
      setUpAudio(null);
    };
  }, []);

  const onLoad = useCallback(img => {
    imgRef.current = img;
  }, []);

  const makeClientCrop = () => {
    if (imgRef && crop.width && crop.height) {
      onChange(createCropPreview(imgRef.current, crop));
      setCropping(false);
      setUpImg(null);
      setCrop(originalDimensions);
    }
  };
  const saveAudio = () => {
    const val = value
      ? [
          ...value,
          {
            name: currentFileName,
            url: upAudio,
            description: '-',
            mediaType: MEDIA_TYPE_API_ENUMS.AUDIO,
          },
        ]
      : [
          {
            name: currentFileName,
            url: upAudio,
            description: '-',
            mediaType: MEDIA_TYPE_API_ENUMS.AUDIO,
          },
        ];
    if (upAudio) {
      onChange(val);
    }
    setCropping(false);
    setUpAudio(null);
    setCrop(originalDimensions);
  };

  const createCropPreview = (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = Math.round(crop.width);
    canvas.height = Math.round(crop.height);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      imgRef.current,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      Math.round(crop.width),
      Math.round(crop.height),
    );
    return value
      ? [
          ...value,
          {
            name: currentFileName,
            url: canvas.toDataURL('image/jpeg', 1),
            description: '-',
            mediaType: MEDIA_TYPE_API_ENUMS.IMAGE,
            height: Math.round(crop.height),
            width: Math.round(crop.width),
          },
        ]
      : [
          {
            name: currentFileName,
            url: canvas.toDataURL('image/jpeg', 1),
            description: '-',
            mediaType: MEDIA_TYPE_API_ENUMS.IMAGE,
            height: Math.round(crop.height),
            width: Math.round(crop.width),
          },
        ];
  };

  const handleCapture = ({ target }) => {
    if (
      formProps?.values?.media &&
      formProps.values.media.length >= noOfMediaFilesAllowed
    ) {
      // if more than files allowed media files are uploaded show snackbar
      snackBar({
        typeOfSnackbar: 'error',
        message: `${t(mediaUploader.filesAllowed)} ${noOfMediaFilesAllowed}`,
      });
      return;
    }

    if (target.files[0].type.includes('audio')) {
      if (target.files[0].size > 2000000) {
        //Greater then 2Mb show snackbar
        snackBar({
          typeOfSnackbar: 'error',
          message: t(mediaUploader.uploadSize),
        });
        return;
      }
      convertToBase64({
        file: target.files[0],
        fn: setUpAudio,
      });
      setCurrentFileName(target.files[0].name);
      setCropping(true);
      target.value = '';
      return;
    }

    resize({
      file: target.files[0],
      maxWidth: window.innerWidth - 100,
      maxHeight: window.innerHeight - 100,
      fn: setUpImg,
    });

    setCurrentFileName(target.files[0].name);
    setCropping(true);
    target.value = '';
  };

  const handleClick = () => {
    inputFile.current.click();
  };

  const checkForAudio = () => {
    return displaySelectedMedia && displaySelectedMedia.includes('audio');
  };

  const isAudio = checkForAudio();
  const checkMediaType = mediaType => {
    // check if mediaType has both image and audio
    if (
      mediaType.includes(MEDIA_TYPE_API_ENUMS.AUDIO) &&
      mediaType.includes(MEDIA_TYPE_API_ENUMS.IMAGE)
    ) {
      return 'image/*, audio/,.mp3,.m4a,.wav,mpeg4';
      // check if it has only image
    } else if (mediaType.includes(MEDIA_TYPE_API_ENUMS.IMAGE)) {
      return 'image/*';
    } else if (mediaType.includes(MEDIA_TYPE_API_ENUMS.AUDIO)) {
      return 'audio/,.mp3,.m4a,.wav,mpeg4';
    } else {
      return 'image/*';
    }
  };

  const setSource = ({ mediaType, displaySelectedMedia, isAudio }) => {
    if (!displaySelectedMedia) {
      // incase there is no source
      if (
        mediaType.includes(MEDIA_TYPE_API_ENUMS.AUDIO) &&
        mediaType.includes(MEDIA_TYPE_API_ENUMS.IMAGE)
      ) {
        return images['addMedia'].default;
      } else if (mediaType.includes(MEDIA_TYPE_API_ENUMS.AUDIO)) {
        return images['audioMediaPlaceholder'].default;
      }
      return images['emptyProfile'].default;
    } else {
      if (isAudio) {
        return images['audioMediaPlaceholder'].default;
      }
      return displaySelectedMedia;
    }
  };
  const setTitle = ({ mediaType, displaySelectedMedia, isAudio }) => {
    if (!displaySelectedMedia) {
      if (
        mediaType.includes(MEDIA_TYPE_API_ENUMS.AUDIO) &&
        mediaType.includes(MEDIA_TYPE_API_ENUMS.IMAGE)
      ) {
        return staticText.general.add_media;
      } else if (mediaType.includes(MEDIA_TYPE_API_ENUMS.AUDIO)) {
        return `${t('Add')} ${t('Audio')}`;
      }
      return staticText.general.add_image;
    } else {
      if (isAudio) {
        return staticText.general.delete_audio;
      }
      return staticText.general.replace_image;
    }
  };

  return (
    <>
      <InputLabel
        shrink
        className={classes.imageLabel}
        required={textErrorOrBool}
        htmlFor="component-error"
      >
        {t(label)}
        <span className={classes.error}>{textErrorOrBool}</span>
      </InputLabel>
      <div className={classes.container}>
        {disabled && <div className={classes.mask}></div>}
        <input
          accept={checkMediaType(mediaType)}
          className={classes.input}
          ref={inputFile}
          onChange={!disabled && handleCapture}
          type="file"
        />
        <Image
          src={setSource({ mediaType, displaySelectedMedia, isAudio })}
          id={id}
          className={classes.image}
          onClick={handleClick}
          title={setTitle({
            mediaType,
            displaySelectedMedia,
            isAudio,
          })}
        />
        {isAudio && (
          <SingleAudioPlayer condense={false} value={displaySelectedMedia} />
        )}
        <Image
          src={images.editIcon.default}
          onClick={!disabled && handleClick}
          className={classes.icon}
          title={setTitle({
            mediaType,
            displaySelectedMedia,
            isAudio,
          })}
        />
      </div>

      {cropping && upImg && allowDeleteRow && (
        <div className={classes.cropContainer}>
          <div className={classes.header}>
            <h2>{t('Crop Image')}</h2>
            <section className={classes.cropButtons}>
              <IconWithText
                component={HighlightOffIcon}
                onClick={() => {
                  setCropping(false);
                  setUpImg(null);
                  setCrop(originalDimensions);
                }}
                title={t(formLabels.cropModalTitle)}
                theme={'light'}
              />
              <IconWithText
                component={WithColorSaveIcon}
                onClick={makeClientCrop}
                title={t(formLabels.cropModalUpdateButton)}
                theme={'light'}
              />
            </section>
          </div>
          <div className={classes.canvas}>
            <ReactCrop
              src={upImg}
              onImageLoaded={onLoad}
              crop={crop}
              onChange={c => setCrop(c)}
              className={classes.fullHeight}
              imageStyle={{ height: '100%' }}
            />
          </div>
        </div>
      )}
      {cropping && upAudio && allowDeleteRow && (
        <div className={classes.audioContainer}>
          <div className={classes.header}>
            <h2>{t('Preview audio')}</h2>
            <section className={classes.cropButtons}>
              <IconWithText
                component={BorderedCloseIcon}
                onClick={() => {
                  setCropping(false);
                  setUpAudio(null);
                }}
                title={formLabels.cropModalTitle}
                theme={'light'}
              />
              <IconWithText
                component={SaveIcon}
                onClick={saveAudio}
                title={t(formLabels.cropModalUpdateButton)}
                theme={'light'}
              />
            </section>
          </div>
          <ReactAudioPlayer base64Audio={upAudio} autoPlay={false} />
        </div>
      )}
    </>
  );
};

export default DetailMediaUploader;

DetailMediaUploader.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.array,
  label: PropTypes.string,
  error: PropTypes.string,
  allowDeleteRow: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  displaySelectedMedia: PropTypes.string,
  mediaType: PropTypes.arrayOf(PropTypes.oneOf(['IMAGE', 'AUDIO'])),
  formProps: PropTypes.object,
  noOfMediaFilesAllowed: PropTypes.number,
};
